import {
  INITIAL_CONFIG as CORE_INITIAL_CONFIG,
  LString,
  type RemoteConfig as CoreRemoteConfig,
} from "@web-solutions/core/constants/remote-config";
import { InterButtonConfig } from "@web-solutions/core/payment/constants";
import { CoreSummarySection } from "@web-solutions/core/summary/types";

export enum SummarySection {
  MONEY_BACK = "money_back",
}

export type MagicProgressType = "circle" | "line";

interface LocalizedString {
  en: string;
  es: string;
  fr: string;
  pt: string;
  de: string;
  tr: string;
  it: string;
  cs: string;
  ro: string;
  pl: string;
  id: string;
}

interface LocalizedArray {
  en: string[];
  es: string[];
  fr: string[];
  pt: string[];
  de: string[];
  tr: string[];
  it: string[];
  cs: string[];
  ro: string[];
  pl: string[];
  id: string[];
}

export interface ImageSource {
  src: string;
  srcSet?: string;
  themes?: {
    [theme: string]: {
      src: string;
      srcSet?: string;
    };
  };
}

export interface ImagesMap {
  [key: string]: ImageSource;
}

interface IQFindOut {
  title: LocalizedString;
  texts: LocalizedString[];
  images: ImageSource[];
}

interface IQUncoverScore {
  title: LocalizedString;
  subTitle: LocalizedString;
  texts: LocalizedArray;
}

interface IQBenefits {
  RESULT: {
    title: LocalizedString;
    text: LocalizedString;
  };
  SERTIFICATE: {
    title: LocalizedString;
    text: LocalizedString;
  };
  PROGRAM: {
    title: LocalizedString;
    text: LocalizedString;
  };
  REPORT: {
    title: LocalizedString;
    text: LocalizedString;
  };
}

interface IQUsers {
  JAMES: {
    title: LocalizedString;
    text: LocalizedString;
    name: LocalizedString;
  };
  ANGELA: {
    title: LocalizedString;
    text: LocalizedString;
    name: LocalizedString;
  };
  KAROLINA: {
    title: LocalizedString;
    text: LocalizedString;
    name: LocalizedString;
  };
}

interface IQTestResults {
  name: LocalizedString;
  time: LocalizedString;
}

interface IQFAQQuestions {
  title: LocalizedString;
  text: LocalizedString;
}

interface IQStructure {
  score: LocalizedString;
  discountTimer: LocalizedString;
  findOut: IQFindOut;
  uncoverScore: IQUncoverScore;
  whatYouGet: {
    title: LocalizedString;
    benefits: IQBenefits;
    images: ImagesMap;
  };
  testers: {
    title: LocalizedString;
    verified: LocalizedString;
    users: IQUsers;
  };
  FAQ: {
    title: LocalizedString;
    questions: Array<IQFAQQuestions>;
  };
  testResults: {
    title: LocalizedString;
    results: IQTestResults[];
  };
  plan: {
    title: LocalizedString;
  };
  moneyBack: {
    title: LocalizedString;
    prelink: LocalizedString;
    link: LocalizedString;
    description: LocalizedString;
  };
  payment_description_short: LocalizedString;
  payment_description: LocalizedString;
  payment_description_contact_us: LocalizedString;
  payment_description_short_more: LocalizedString;
  payment_description_trial: LocalizedString;
  payment_description_intr: LocalizedString;
  payment_description_short_trial: LocalizedString;
  payment_description_short_intr: LocalizedString;
  payment_description_short_link: LocalizedString;
  payment_description_bank_statement: LocalizedString;
  payment_description_phone: LocalizedString;
  priceTexts: {
    P1D: LocalizedString;
    P1Y: LocalizedString;
    P6M: LocalizedString;
    P1M: LocalizedString;
    P3M: LocalizedString;
    P1W: LocalizedString;
    P2W: LocalizedString;
  };
  agreement: {
    policy: LocalizedString;
    privacy_policy: LocalizedString;
    terms: LocalizedString;
    and: LocalizedString;
    billing_terms: LocalizedString;
    money_back: LocalizedString;
  };
  footer: {
    contact_us: LocalizedString;
    privacy_policy: LocalizedString;
    all_rights_reserved: LocalizedString;
    terms: LocalizedString;
    billing_terms: LocalizedString;
    money_back: LocalizedString;
  };
}

export interface RemoteConfig<FlowStep = string>
  extends CoreRemoteConfig<FlowStep> {
  landingStack: Array<SummarySection | CoreSummarySection>;
  landingButtons: Array<InterButtonConfig<SummarySection>>;

  headerTitle: string;
  headerSubTitle: string;

  magicDurationLoad: number;
  magicTitle: LocalizedString;
  magicStages: LString[];
  magicRoundCheckMark: boolean;
  magicType: MagicProgressType;

  emailPageTitle: LocalizedString;
  emailPageSections: SummarySection[];
  emailPageBlurred: boolean;
  quizFlow: string[];
  routeGroups: [];
  quizWelcomePage: {
    title: LocalizedString;
    advantages: LocalizedArray;
    buttonText: LocalizedString;
    magic: {
      subTitle: LocalizedString;
    };
  };

  quizUsersUsingPage: {
    title: LocalizedString;
    description: LocalizedString;
    mentioned: { title: LocalizedString };
    buttonText: LocalizedString;
  };

  feedbacks: {
    title: LocalizedString;
    text: LocalizedString;
    imgKey?: string;
  }[];
  feedbacksTitle: LocalizedString;

  downloadedSection: {
    title: string;
    appstoreTitle: string;
    appstoreRating: string;
    playstoreTitle: string;
    playstoreRating: string;
  };
  purchaseScreenData: IQStructure;
}

export const INITIAL_CONFIG: RemoteConfig = {
  ...CORE_INITIAL_CONFIG,

  landingStack: [],
  landingButtons: [],

  headerTitle: "",
  headerSubTitle: "",

  magicDurationLoad: 8000,
  magicTitle: {
    en: "",
    es: "Spanish",
    fr: "French",
    pt: "Portuguese",
    de: "German",
    tr: "Turkish",
    it: "Italian",
    cs: "Czech",
    ro: "Romanian",
    pl: "Polish",
    id: "Indonesian",
  },
  magicStages: [],
  magicRoundCheckMark: false,
  magicType: "circle",

  emailPageTitle: {} as LocalizedString,
  emailPageSections: [],
  emailPageBlurred: false,
  quizFlow: [],
  routeGroups: [],
  quizWelcomePage: {
    title: {
      en: "Ready to uncover your\n<b>IQ score?</b>",
      es: "Spanish",
      fr: "French",
      pt: "Portuguese",
      de: "German",
      tr: "Turkish",
      it: "Italian",
      cs: "Czech",
      ro: "Romanian",
      pl: "Polish",
      id: "Indonesian",
    },
    advantages: {
      en: [
        "Test your intelligence with 30 questions of increasing difficulty",
        "Pick the right answer out of 6 possible options",
        "If you get stuck, just skip a question and come back to it later",
      ],
    } as LocalizedArray,
    buttonText: {
      en: "Start Certified Test",
      es: "Spanish",
      fr: "French",
      pt: "Portuguese",
      de: "German",
      tr: "Turkish",
      it: "Italian",
      cs: "Czech",
      ro: "Romanian",
      pl: "Polish",
      id: "Indonesian",
    },
    magic: {
      subTitle: {
        en: "Just a moment, our AI is analyzing your answers against 5 areas of intelligence...",
        es: "Spanish",
        fr: "French",
        pt: "Portuguese",
        de: "German",
        tr: "Turkish",
        it: "Italian",
        cs: "Czech",
        ro: "Romanian",
        pl: "Polish",
        id: "Indonesian",
      },
    },
  },

  quizUsersUsingPage: {
    title: {
      en: "Over 30,000 women\n<b>are using the Soula App</b>",
      es: "",
      fr: "",
      pt: "",
      de: "",
      tr: "",
      it: "",
      cs: "",
      ro: "",
      pl: "",
      id: "",
    },
    description: {
      en: "We are committed to helping women around the world create their new identity with confidence, peace and happiness.",
      es: "",
      fr: "",
      pt: "",
      de: "",
      tr: "",
      it: "",
      cs: "",
      ro: "",
      pl: "",
      id: "",
    },
    mentioned: {
      title: {
        en: "Mentioned in",
        es: "",
        fr: "",
        pt: "",
        de: "",
        tr: "",
        it: "",
        cs: "",
        ro: "",
        pl: "",
        id: "",
      },
    },
    buttonText: {
      en: "Continue",
      es: "",
      fr: "",
      pt: "",
      de: "",
      tr: "",
      it: "",
      cs: "",
      ro: "",
      pl: "",
      id: "",
    },
  },

  feedbacks: [],
  feedbacksTitle: {} as LocalizedString,

  downloadedSection: {
    title: "",
    appstoreTitle: "",
    appstoreRating: "",
    playstoreTitle: "",
    playstoreRating: "",
  },
  purchaseScreenData: {} as IQStructure,
};
